import React, { useMemo, useState } from 'react';
import { EmptyState } from '../ui/EmptyState';
import { useEscToClose } from '../lib/useEscToClose';
import { timeAgo } from '../lib/timeAgo';

/**
 * PEOPLE, NOT VENUES (Evan 2026-09-28: *"we should be tracking distinct owners/managers that
 * own/manage different places - add a button do do this to the crm page"*).
 *
 * The CRM is venue-first, so a hospitality group that runs four bars reads as four unrelated rows.
 * Commercially that is backwards: one conversation closes four venues, one angry owner puts four at
 * risk. This flips the axis — one card per person, every venue they touch underneath it.
 *
 * ⚠️ READ-ONLY, DELIBERATELY. There is no person entity in the database; this grouping is inferred
 * from email strings scattered across four tables, and it is wrong in both directions (see
 * `GROUPING_NOTE` and the migration header at
 * `supabase/migrations/20260929100000_admin_list_venue_people.sql`). Hanging edit controls off a
 * grouping nobody has eyeballed yet is how real relationship data gets corrupted. The only
 * interaction here is navigation: click a venue to open its CRM drawer.
 */

/** One (person, venue, attachment) row exactly as `admin_list_venue_people` returns it. */
export type VenuePersonRow = {
  person_key: string;
  email: string;
  display_name: string | null;
  is_staff: boolean;
  shared_mailbox: boolean;
  venue_count: number;
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  attachment: 'portal_access' | 'claim' | 'interest' | 'outreach_contact' | string;
  attachment_detail: string | null;
  attachment_active: boolean;
  venue_is_live: boolean;
  first_at: string | null;
  last_seen_at: string | null;
  last_activity_at: string | null;
};

const GROUPING_NOTE =
  'Grouped by email address — one card per mailbox, not per human. Two addresses for the same person show up as two people; a shared inbox like info@ shows up as one person who may be several.';

/** How a person is attached to a venue, strongest first — the order also drives the venue rows. */
const ATTACHMENT_RANK: Record<string, number> = {
  portal_access: 0,
  claim: 1,
  interest: 2,
  outreach_contact: 3,
};

const ATTACHMENT_LABEL: Record<string, string> = {
  portal_access: 'Portal access',
  claim: 'Claim',
  interest: 'Interest',
  outreach_contact: 'Outreach',
};

const ATTACHMENT_TONE: Record<string, string> = {
  portal_access: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  claim: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  interest: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  outreach_contact: 'border-border bg-white/[0.04] text-muted',
};

function attachmentLabel(kind: string): string {
  return ATTACHMENT_LABEL[kind] ?? kind;
}

function ago(iso: string | null): string {
  return iso ? timeAgo(iso) : '—';
}

/** A person, assembled from their attachment rows. */
type Person = {
  key: string;
  email: string;
  name: string | null;
  isStaff: boolean;
  sharedMailbox: boolean;
  kinds: string[];
  venues: {
    venueId: string;
    name: string;
    city: string | null;
    isLive: boolean;
    /** Every way this person is attached to THIS venue — a portal login and a cold email both count. */
    attachments: { kind: string; detail: string | null; active: boolean }[];
    lastSeenAt: string | null;
    lastActivityAt: string | null;
  }[];
  lastActivityAt: string | null;
};

/**
 * Fold the flat attachment rows into people → venues.
 *
 * ⚠️ Grouping happens on `person_key`, never on `email`. They are the same string today, but the
 * migration keeps them as separate columns precisely so a future manual "these are the same person"
 * merge can redefine the key without this component changing.
 */
function buildPeople(rows: VenuePersonRow[]): Person[] {
  const byPerson = new Map<string, Person>();

  for (const r of rows) {
    let p = byPerson.get(r.person_key);
    if (!p) {
      p = {
        key: r.person_key,
        email: r.email,
        name: r.display_name,
        isStaff: r.is_staff,
        sharedMailbox: r.shared_mailbox,
        kinds: [],
        venues: [],
        lastActivityAt: null,
      };
      byPerson.set(r.person_key, p);
    }
    if (!p.kinds.includes(r.attachment)) p.kinds.push(r.attachment);

    let v = p.venues.find((x) => x.venueId === r.venue_id);
    if (!v) {
      v = {
        venueId: r.venue_id,
        name: r.venue_name ?? 'Unknown venue',
        city: r.city_name,
        isLive: r.venue_is_live,
        attachments: [],
        lastSeenAt: null,
        lastActivityAt: null,
      };
      p.venues.push(v);
    }
    v.attachments.push({ kind: r.attachment, detail: r.attachment_detail, active: r.attachment_active });
    if (r.last_seen_at && (!v.lastSeenAt || r.last_seen_at > v.lastSeenAt)) v.lastSeenAt = r.last_seen_at;
    if (r.last_activity_at && (!v.lastActivityAt || r.last_activity_at > v.lastActivityAt)) {
      v.lastActivityAt = r.last_activity_at;
    }
    if (r.last_activity_at && (!p.lastActivityAt || r.last_activity_at > p.lastActivityAt)) {
      p.lastActivityAt = r.last_activity_at;
    }
  }

  /* `Array.from`, not spread — this project compiles to ES5 and a spread over a Map iterator does
     not survive that target (TS2802). */
  const people = Array.from(byPerson.values());
  for (const p of people) {
    p.kinds.sort((a, b) => (ATTACHMENT_RANK[a] ?? 9) - (ATTACHMENT_RANK[b] ?? 9));
    for (const v of p.venues) {
      v.attachments.sort((a, b) => (ATTACHMENT_RANK[a.kind] ?? 9) - (ATTACHMENT_RANK[b.kind] ?? 9));
    }
    p.venues.sort(
      (a, b) =>
        Number(b.isLive) - Number(a.isLive) || (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''),
    );
  }

  /* The whole point of the screen is "who covers several places" — so they go first. */
  people.sort(
    (a, b) =>
      Number(a.isStaff) - Number(b.isStaff) ||
      b.venues.length - a.venues.length ||
      (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''),
  );
  return people;
}

function Pill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}>
      {children}
    </span>
  );
}

function PersonCard({
  person,
  onOpenVenue,
}: {
  person: Person;
  onOpenVenue?: (venueId: string) => void;
}) {
  const multi = person.venues.length > 1;
  return (
    <div className={`admin-card ${multi ? 'border-accent/35' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="admin-label-primary truncate text-[13px]">
            {person.name ?? person.email}
          </div>
          {person.name ? (
            <div className="mt-0.5 truncate text-[11px] text-mutedDim">{person.email}</div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {/* The headline fact. Accent only when it is more than one — a "1 venue" pill is noise. */}
          <Pill
            tone={
              multi
                ? 'border-accent/50 bg-accent/15 text-accent'
                : 'border-border bg-white/[0.04] text-mutedDim'
            }
          >
            {person.venues.length} venue{person.venues.length === 1 ? '' : 's'}
          </Pill>
          {person.kinds.map((k) => (
            <Pill key={k} tone={ATTACHMENT_TONE[k] ?? ATTACHMENT_TONE.outreach_contact}>
              {attachmentLabel(k)}
            </Pill>
          ))}
          {person.isStaff ? (
            <Pill tone="border-violet-500/40 bg-violet-500/10 text-violet-200">Shado staff</Pill>
          ) : null}
        </div>
      </div>

      {/*
        ⚠️ THE MERGE FAILURE, SAID OUT LOUD. A role mailbox is one address several humans read, so
        this card may be a team rather than a person. No rule can tell them apart, so the card
        admits it rather than quietly presenting the group as one relationship.
      */}
      {person.sharedMailbox ? (
        <p className="mt-2 text-[11px] leading-relaxed text-amber-200/80">
          Shared inbox — this address may be read by more than one person.
        </p>
      ) : null}

      <div className="mt-3 divide-y divide-border/60 border-t border-border/60">
        {person.venues.map((v) => {
          const clickable = Boolean(onOpenVenue);
          return (
            <div
              key={v.venueId}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onOpenVenue?.(v.venueId) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenVenue?.(v.venueId);
                      }
                    }
                  : undefined
              }
              className={`flex flex-wrap items-center justify-between gap-2 py-2.5 ${
                clickable ? 'cursor-pointer transition-colors hover:bg-white/[0.03]' : ''
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[12px] text-textPrimary">{v.name}</span>
                  {v.isLive ? (
                    <Pill tone="border-emerald-500/35 bg-emerald-500/10 text-emerald-200">Live</Pill>
                  ) : (
                    <Pill tone="border-border bg-white/[0.04] text-mutedDim">Not live</Pill>
                  )}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-mutedDim">
                  {[
                    v.city,
                    ...v.attachments.map((a) =>
                      [attachmentLabel(a.kind), a.detail].filter(Boolean).join(' · '),
                    ),
                  ]
                    .filter(Boolean)
                    .join('  ·  ')}
                </div>
              </div>
              <div className="shrink-0 text-right text-[11px] text-mutedDim">
                {/* Last seen is a real sign-in; last activity is any signal at all. Different facts. */}
                <div>{v.lastSeenAt ? `signed in ${ago(v.lastSeenAt)}` : 'never signed in'}</div>
                <div className="text-[10px]">last activity {ago(v.lastActivityAt)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function VenuePeopleModal({
  open,
  rows,
  loading,
  onClose,
  onOpenVenue,
}: {
  open: boolean;
  rows: VenuePersonRow[];
  loading: boolean;
  onClose: () => void;
  /** Navigation only — opens that venue's existing CRM drawer. No writes happen from this modal. */
  onOpenVenue?: (venueId: string) => void;
}) {
  const [query, setQuery] = useState('');
  /*
   * Staff rows are Shado's own "Get access" grants (`admin_open_venue_portal` writes the admin's
   * email against the venue). They are hidden by default because they are not relationships — but
   * they are HIDDEN, not dropped, so the list can never quietly disagree with the database.
   */
  const [showStaff, setShowStaff] = useState(false);

  useEscToClose(open, onClose);

  const people = useMemo(() => buildPeople(rows), [rows]);
  const staffCount = useMemo(() => people.filter((p) => p.isStaff).length, [people]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter((p) => {
      if (p.isStaff && !showStaff) return false;
      if (!q) return true;
      return (
        p.email.includes(q) ||
        (p.name ?? '').toLowerCase().includes(q) ||
        p.venues.some(
          (v) => v.name.toLowerCase().includes(q) || (v.city ?? '').toLowerCase().includes(q),
        )
      );
    });
  }, [people, query, showStaff]);

  const multiCount = useMemo(() => visible.filter((p) => p.venues.length > 1).length, [visible]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="People"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-xl border border-border bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="admin-label-primary text-base">
              People
              {!loading ? ` · ${visible.length.toLocaleString()}` : ''}
            </h2>
            <p className="mt-1 max-w-xl text-[11px] leading-relaxed text-mutedDim">{GROUPING_NOTE}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
          >
            Close
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, venue or city"
            className="admin-input w-64 text-xs"
          />
          <span className="text-[11px] text-mutedDim">
            {multiCount > 0
              ? `${multiCount} cover more than one venue`
              : 'Nobody covers more than one venue yet'}
          </span>
          {staffCount > 0 ? (
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-[11px] text-mutedDim">
              <input
                type="checkbox"
                checked={showStaff}
                onChange={(e) => setShowStaff(e.target.checked)}
                className="accent-accent"
              />
              Show {staffCount} Shado staff grant{staffCount === 1 ? '' : 's'}
            </label>
          ) : null}
        </div>

        <div className="max-h-[70vh] space-y-3 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted">Loading people…</p>
          ) : visible.length === 0 ? (
            <EmptyState
              compact
              title={query ? 'Nobody matches' : 'No venue people yet'}
              hint={
                query
                  ? 'Try the venue name, the city, or part of the email address.'
                  : 'People appear here once someone is granted portal access, claims a venue, registers interest, or is added to the outreach address book.'
              }
            />
          ) : (
            visible.map((p) => <PersonCard key={p.key} person={p} onOpenVenue={onOpenVenue} />)
          )}
        </div>
      </div>
    </div>
  );
}
